// Per-sheet fingerprints for index freshness (PLAN-agent-v2 §11.3, #21). Pure: no Office.js.
//
// A fingerprint is a deterministic hash of everything the user can change on a sheet that the index is measured
// from: the used range and its size, the `formulas` of EVERY cell of the used range (the formula text, else the
// constant typed in), the hidden rows and columns, and the table and chart counts. No sampling: a value typed in
// the last row of the last column moves it. Recalculated results, number formats and styles do not.
// The executor reads the parts (`readFingerprint`) and the backend compares the value it receives in every turn's
// context with the one stored in the index to detect stale sheets.

export interface FingerprintParts {
  usedRange: string;
  cellCount: number;
  /** `range.formulas` of the whole used range, row by row. */
  formulas: readonly (readonly unknown[])[];
  /** Hidden row / column spans ("5:7", "D:E"), as `hiddenSpans` returns them. */
  hiddenRows: readonly string[];
  hiddenCols: readonly string[];
  tableCount: number;
  chartCount: number;
}

const RECORD_SEP = '\u001e';
const UNIT_SEP = '\u001f';
const FNV_PRIME = 0x01000193;
const OFFSET_A = 0x811c9dc5;
const OFFSET_B = 0x050c5d1f;

/** FNV-1a 32-bit over a UTF-16 code-unit string, with a configurable offset basis. */
export function fnv1a32(text: string, offset = OFFSET_A): number {
  let h = offset >>> 0;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    // 32-bit multiply by the FNV prime (16777619) without overflowing double precision.
    h = Math.imul(h, FNV_PRIME) >>> 0;
  }
  return h >>> 0;
}

/** Two FNV-1a 32-bit hashes fed piece by piece: a sheet of 100 000 cells is never joined into one string. */
class PairHasher {
  private a = OFFSET_A;
  private b = OFFSET_B;

  feed(text: string): void {
    let a = this.a;
    let b = this.b;
    for (let i = 0; i < text.length; i++) {
      const c = text.charCodeAt(i);
      a = Math.imul(a ^ c, FNV_PRIME) >>> 0;
      b = Math.imul(b ^ c, FNV_PRIME) >>> 0;
    }
    this.a = a;
    this.b = b;
  }

  hex(): string {
    return this.a.toString(16).padStart(8, '0') + this.b.toString(16).padStart(8, '0');
  }
}

/** Deterministic 16-hex-char fingerprint of a sheet. Cells are separated by UNIT_SEP, rows and sections by
 *  RECORD_SEP, so moving a value across a cell or row boundary changes the hash. */
export function fingerprintSheet(parts: FingerprintParts): string {
  const h = new PairHasher();
  h.feed([parts.usedRange, String(parts.cellCount), String(parts.tableCount), String(parts.chartCount)].join(UNIT_SEP));
  h.feed(RECORD_SEP);
  h.feed(parts.hiddenRows.join(UNIT_SEP));
  h.feed(RECORD_SEP);
  h.feed(parts.hiddenCols.join(UNIT_SEP));
  h.feed(RECORD_SEP);
  for (const row of parts.formulas) {
    h.feed(RECORD_SEP);
    for (let c = 0; c < row.length; c++) {
      if (c > 0) h.feed(UNIT_SEP);
      const cell = row[c];
      if (cell !== null && cell !== undefined) h.feed(String(cell));
    }
  }
  return h.hex();
}
